import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Divider,
  Flex,
  Spinner,
  useToast,
  Switch,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { usePricing } from '../../hooks/usePricing';

// Plan metadata (features, descriptions) - prices come from Stripe
const PLAN_METADATA = [
  {
    id: 'basic',
    name: 'Basic',
    description: 'For small teams of 3-6 people',
    features: [
      { text: 'Access to 3 integrations', included: true },
      { text: '1 concurrent meeting', included: true },
      { text: '30h of meetings monthly', included: true },
      { text: '150 smart tasks', included: true },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For medium teams of 7-20 people',
    features: [
      { text: 'Access to 6 integrations', included: true },
      { text: '3 concurrent meetings', included: true },
      { text: '80h of meetings monthly', included: true },
      { text: '400 smart tasks', included: true },
    ],
    popular: true,
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    description: 'For large enterprise teams of 20+ members',
    features: [
      { text: 'Access to unlimited integrations', included: true },
      { text: '10 concurrent meetings', included: true },
      { text: '145h of meetings monthly', included: true },
      { text: '800 smart tasks', included: true },
    ],
  },
];

export default function SubscriptionSelector({ 
  agentId, 
  onSubscriptionComplete, 
  isEarlyAccess = true,
  showWarning = false,
  warningMessage = null 
}) {
  const toast = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [billingInterval, setBillingInterval] = useState('annual'); // 'monthly' or 'annual'
  const { pricing, loading: pricingLoading, error: pricingError } = usePricing();

  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'navy.600');
  const selectedBorderColor = useColorModeValue('brand.500', 'brand.400');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.700', 'secondaryGray.600');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');

  // Merge pricing data with plan metadata
  const plans = PLAN_METADATA.map((meta) => {
    const planPricing = pricing?.[meta.id];
    
    // Get base prices from Stripe (these are the full rack prices)
    const monthlyPublicPrice = planPricing?.monthly?.amount || 0;
    const annualPublicYearly = planPricing?.annual?.amount || 0;
    
    // Calculate early access prices (50% off the rack price) - round to 2 decimals
    const monthlyDiscountedPrice = Math.round(monthlyPublicPrice * 0.5 * 100) / 100;
    const annualDiscountedYearly = Math.round(annualPublicYearly * 0.5 * 100) / 100;
    
    // Calculate monthly equivalent for annual pricing (divide yearly by 12) - round to 2 decimals
    const annualPublicPrice = annualPublicYearly > 0 ? Math.round((annualPublicYearly / 12) * 100) / 100 : monthlyPublicPrice;
    const annualDiscountedPrice = annualDiscountedYearly > 0 ? Math.round((annualDiscountedYearly / 12) * 100) / 100 : monthlyDiscountedPrice;
    
    return {
      ...meta,
      monthlyPublicPrice,
      monthlyDiscountedPrice,
      annualPublicPrice,
      annualDiscountedPrice,
      // Store the actual yearly billing total
      annualBillingTotal: annualDiscountedYearly > 0 ? annualDiscountedYearly : Math.round(monthlyDiscountedPrice * 12 * 100) / 100,
      limits: planPricing?.limits || {},
      payAsYouGo: {
        meetingHour: planPricing?.overages?.meetingHour?.amount || 0,
        smartTasks: planPricing?.overages?.smartTasks25?.amount || 0,
      },
    };
  });

  const handleSelectPlan = async (planId) => {
    if (!agentId) {
      toast({
        status: 'error',
        title: 'Agent ID required',
        description: 'Please create an agent first',
      });
      return;
    }

    setSelectedPlan(planId);
    setIsCreatingCheckout(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const functions = getFunctions();
      const createCheckoutUrl = `https://us-central1-polletask-dev.cloudfunctions.net/billing-createCheckoutSessionHttp`;

      const response = await fetch(createCheckoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          agentId,
          plan: planId,
          billingInterval,
          isEarlyAccess,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        status: 'error',
        title: 'Failed to start checkout',
        description: error.message || 'Please try again',
        duration: 5000,
        isClosable: true,
      });
      setSelectedPlan(null);
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const calculateSavings = (plan) => {
    if (billingInterval === 'annual') {
      // Savings from early access annual pricing (vs public annual)
      const monthlySavings = plan.annualPublicPrice - plan.annualDiscountedPrice;
      const percentOff = plan.annualPublicPrice > 0 
        ? Math.round((monthlySavings / plan.annualPublicPrice) * 100)
        : 0;
      return { amount: monthlySavings, percent: percentOff };
    }
    // Savings from early access pricing (vs public monthly)
    const monthlySavings = plan.monthlyPublicPrice - plan.monthlyDiscountedPrice;
    const percentOff = plan.monthlyPublicPrice > 0
      ? Math.round((monthlySavings / plan.monthlyPublicPrice) * 100)
      : 0;
    return { amount: monthlySavings, percent: percentOff };
  };

  // Show loading state while fetching pricing
  if (pricingLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text color={textColorSecondary}>Loading pricing information...</Text>
        </VStack>
      </Flex>
    );
  }

  // Show error state if pricing failed to load
  if (pricingError || !pricing) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Text color="red.500" fontWeight="bold">Failed to load pricing</Text>
          <Text color={textColorSecondary} fontSize="sm">{pricingError || 'Please try again later'}</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box textAlign="center">
        <Text fontSize="3xl" fontWeight="bold" color={textColor} mb={2}>
          Choose Your Plan
        </Text>
        <Text fontSize="md" color={textColorSecondary} mb={2}>
          Start with a 10-day free trial. No credit card required until trial ends.
        </Text>
        <Text fontSize="sm" color={textColorSecondary} fontStyle="italic">
          All plans include voice capabilities, smart summaries, and access to your team's tools
        </Text>
      </Box>

      {/* Billing Interval Toggle */}
      <Flex justify="center" align="center" gap={4}>
        <Text color={billingInterval === 'monthly' ? textColor : textColorSecondary} fontWeight="medium">
          Monthly
        </Text>
        <Switch
          size="lg"
          colorScheme="brand"
          isChecked={billingInterval === 'annual'}
          onChange={(e) => setBillingInterval(e.target.checked ? 'annual' : 'monthly')}
        />
        <Text color={billingInterval === 'annual' ? textColor : textColorSecondary} fontWeight="medium">
          Annual
        </Text>
        {billingInterval === 'annual' && (
          <Badge colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="md">
            Save 17%
          </Badge>
        )}
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
        {plans.map((plan) => {
          const currentPrice = billingInterval === 'annual' 
            ? plan.annualDiscountedPrice 
            : plan.monthlyDiscountedPrice;
          const publicPrice = billingInterval === 'annual'
            ? plan.annualPublicPrice
            : plan.monthlyPublicPrice;
          const savings = calculateSavings(plan);
          const isSelected = selectedPlan === plan.id;

          return (
            <Box
              key={plan.id}
              p={8}
              bg={cardBg}
              borderWidth={plan.popular ? 3 : 2}
              borderColor={plan.popular ? 'blue.500' : (isSelected ? selectedBorderColor : borderColor)}
              borderRadius="xl"
              position="relative"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: plan.popular ? 'blue.400' : selectedBorderColor,
                transform: 'translateY(-4px)',
                boxShadow: 'xl',
              }}
              onClick={() => !isCreatingCheckout && handleSelectPlan(plan.id)}
            >
              {plan.popular && (
                <Badge
                  position="absolute"
                  top={-3}
                  left="50%"
                  transform="translateX(-50%)"
                  colorScheme="blue"
                  fontSize="xs"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  Most Popular
                </Badge>
              )}

              <VStack align="stretch" spacing={4}>
                <Box textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {plan.name}
                  </Text>
                  <Text fontSize="sm" color={textColorSecondary} mt={2} minH="40px">
                    {plan.description}
                  </Text>
                </Box>

                <Box textAlign="center">
                  <Text fontSize="sm" color={textColorSecondary} textDecoration="line-through">
                    ${publicPrice} /mo
                  </Text>
                  <Text fontSize="xs" color="blue.400" fontWeight="bold" mb={2}>
                    LIMITED TIME OFFER
                  </Text>
                  <HStack justify="center" align="baseline" spacing={1}>
                    <Text fontSize="4xl" fontWeight="bold" color={textColor}>
                      ${currentPrice}
                    </Text>
                    <Text fontSize="md" color={textColorSecondary}>
                      /mo
                    </Text>
                  </HStack>
                  {billingInterval === 'annual' && (
                    <Text fontSize="xs" color={textColorSecondary} mt={1}>
                      Billed annually (${plan.annualBillingTotal}/yr)
                    </Text>
                  )}
                  <Text fontSize="xs" color="green.500" fontWeight="semibold" mt={2}>
                    Save ${savings.amount}/mo ({savings.percent}% off)
                  </Text>
                </Box>

                <Divider />

                <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                  {plan.features.map((feature, idx) => (
                    <HStack key={idx} align="flex-start" spacing={2}>
                      <Icon 
                        as={feature.included ? CheckIcon : CloseIcon} 
                        color={feature.included ? 'blue.400' : 'gray.500'} 
                        mt={1} 
                        boxSize={3} 
                      />
                      <Text 
                        fontSize="sm" 
                        color={feature.included ? textColorSecondary : 'gray.500'}
                        textDecoration={feature.included ? 'none' : 'line-through'}
                      >
                        {feature.text}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                <Divider />

                <Box>
                  <Text fontSize="xs" fontWeight="bold" color={textColor} mb={1}>
                    Pay-as-you-go:
                  </Text>
                  <Text fontSize="xs" color={textColorSecondary}>
                    Extra meeting hour: ${plan.payAsYouGo.meetingHour.toFixed(2)} / h
                  </Text>
                  <Text fontSize="xs" color={textColorSecondary}>
                    +25 smart tasks: ${plan.payAsYouGo.smartTasks.toFixed(2)}
                  </Text>
                </Box>

                <Button
                  colorScheme={plan.popular ? 'blue' : 'gray'}
                  variant={plan.popular ? 'solid' : 'outline'}
                  size="lg"
                  width="100%"
                  isLoading={isCreatingCheckout && selectedPlan === plan.id}
                  loadingText="Redirecting..."
                  isDisabled={isCreatingCheckout}
                >
                  Get Started
                </Button>
              </VStack>
            </Box>
          );
        })}
      </SimpleGrid>

      <Box textAlign="center" fontSize="sm" color={textColorSecondary} pt={2}>
        <Text fontWeight="medium">
          All plans include a 10-day free trial. Cancel anytime during the trial period.
        </Text>
        <Text fontSize="xs" mt={1}>
          No credit card required to start your trial
        </Text>
      </Box>
    </VStack>
  );
}

